import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Btn, Modal, Field, inputCls } from "@/components/common";
import { Plus, Pencil, Trash2, ShieldCheck, Building2 } from "lucide-react";

const empty = { username: "", password: "", name: "", role: "agency", agency_name: "" };

export default function Utenti() {
  const [users, setUsers] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    api.get("/agencies").then((r) => setAgencies((r.data || []).filter((a) => a.name !== "Diretto")));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (u) => {
    setForm({ username: u.username, password: "", name: u.name, role: u.role, agency_name: u.agency_name || "" });
    setEditId(u.id);
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editId) {
        const upd = { name: form.name, role: form.role, agency_name: form.agency_name };
        if (form.password) upd.password = form.password;
        await api.put(`/users/${editId}`, upd);
      } else {
        await api.post("/users", form);
      }
      toast.success("Salvato");
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare l'utente?")) return;
    try {
      await api.delete(`/users/${id}`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title="Utenti" subtitle="Amministratore, manager e agenzie con diversi diritti">
        <Btn data-testid="add-user-btn" onClick={openNew}><Plus size={16} /> Nuovo utente</Btn>
      </PageHeader>

      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase text-slate-500 border-b border-slate-200 bg-slate-50">
              <th className="px-3 py-2.5">Utente</th>
              <th className="px-3 py-2.5">Username</th>
              <th className="px-3 py-2.5">Ruolo</th>
              <th className="px-3 py-2.5">Agenzia</th>
              <th className="px-3 py-2.5"></th>
            </tr>
          </thead>
          <tbody data-testid="users-table">
            {users.map((u) => (
              <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="px-3 py-2.5 font-medium text-slate-800">{u.name}</td>
                <td className="px-3 py-2.5 text-slate-600 font-mono">{u.username}</td>
                <td className="px-3 py-2.5">
                  <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${u.role === "admin" ? "bg-sky-100 text-sky-700" : "bg-slate-100 text-slate-600"}`}>
                    {u.role === "admin" ? <ShieldCheck size={12} /> : <Building2 size={12} />}
                    {u.role === "admin" ? "Admin" : u.role === "manager" ? "Manager" : u.role === "fotografo" ? "Fotografo" : u.role === "barman" ? "Barman" : u.role === "staff" ? "Staff" : "Agenzia"}
                  </span>
                </td>
                <td className="px-3 py-2.5 text-slate-600">{u.agency_name || "—"}</td>
                <td className="px-3 py-2.5">
                  <div className="flex gap-1 justify-end">
                    <button data-testid={`edit-user-${u.id}`} onClick={() => openEdit(u)} className="p-1.5 text-slate-400 hover:text-sky-600"><Pencil size={15} /></button>
                    {u.role !== "admin" && (
                      <button data-testid={`del-user-${u.id}`} onClick={() => remove(u.id)} className="p-1.5 text-slate-400 hover:text-red-600"><Trash2 size={15} /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Modifica utente" : "Nuovo utente"}>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Nome">
            <input required value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} data-testid="u-name" />
          </Field>
          {!editId && (
            <Field label="Username">
              <input required value={form.username} onChange={(e) => set("username", e.target.value)} className={inputCls} data-testid="u-username" />
            </Field>
          )}
          <Field label={editId ? "Nuova password (lascia vuoto per non cambiare)" : "Password"}>
            <input type="text" value={form.password} onChange={(e) => set("password", e.target.value)} className={inputCls} data-testid="u-password" required={!editId} />
          </Field>
          <Field label="Ruolo">
            <select value={form.role} onChange={(e) => set("role", e.target.value)} className={inputCls} data-testid="u-role">
              <option value="agency">Agenzia</option>
              <option value="fotografo">Fotografo</option>
              <option value="barman">Barman</option>
              <option value="manager">Manager</option>
              <option value="admin">Amministratore</option>
            </select>
          </Field>
          {form.role === "agency" && (
            <Field label="Agenzia associata (vede/modifica solo le sue prenotazioni)">
              <select value={form.agency_name} onChange={(e) => set("agency_name", e.target.value)} className={inputCls} data-testid="u-agency" required>
                <option value="">Seleziona agenzia…</option>
                {agencies.map((a) => <option key={a.id || a.name} value={a.name}>{a.name}</option>)}
              </select>
            </Field>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Btn type="button" variant="secondary" onClick={() => setOpen(false)}>Annulla</Btn>
            <Btn type="submit" data-testid="save-user">Salva</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
